import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2, AlertTriangle, XCircle, User, CreditCard, Mail, RefreshCw, ArrowRight,
  ClipboardList, Users, UserPlus, Trash2, Hash, Calendar, FileText,
  Building2, Car, BarChart3, ChevronDown, ChevronUp, Send, Globe,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import i18n, { setStoredLanguage } from "@/i18n";
import { intlLocale } from "@/lib/locale";
import type { DealerProfile } from "@shared/schema";
import { ContactUsModal } from "@/components/contact-us-modal";
import { DeanTrainingConsentCard } from "@/components/dean-training-consent";

function statusLabel(status: string | null, t: TFunction) {
  switch (status) {
    case "owner": return { text: t("account:status.owner"), color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" };
    case "active": return { text: t("account:status.active"), color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" };
    case "trialing": return { text: t("account:status.trialing"), color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" };
    case "canceling": return { text: t("account:status.canceling"), color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" };
    case "canceled": return { text: t("account:status.canceled"), color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" };
    case "past_due": return { text: t("account:status.pastDue"), color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" };
    default: return { text: t("account:status.none"), color: "bg-muted text-muted-foreground" };
  }
}

function fmt(timestamp: number | null): string {
  if (!timestamp) return "—";
  return new Date(timestamp * 1000).toLocaleDateString(intlLocale(), {
    month: "long", day: "numeric", year: "numeric",
  });
}

export default function AccountPage() {
  const { toast } = useToast();
  const { t } = useTranslation(["account", "common"]);
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const [cancelDialog, setCancelDialog] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");

  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/change-password", {
        currentPassword: pwCurrent,
        newPassword: pwNew,
      });
    },
    onSuccess: () => {
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      toast({ title: t("account:security.changedTitle"), description: t("account:security.changedDesc") });
    },
    onError: (err: any) => {
      toast({ title: t("account:security.changeFailedTitle"), description: err?.message ?? "", variant: "destructive" });
    },
  });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"viewer" | "editor">("viewer");
  const [invitePermissions, setInvitePermissions] = useState({
    financialAccess: false,
    lenderAccess: false,
    inventoryAccess: false,
    analyticsAccess: false,
  });
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [redeemCode, setRedeemCode] = useState("");

  const { data: profile } = useQuery<DealerProfile | null>({
    queryKey: ["/api/dealer-profile"],
    retry: false,
    staleTime: 60_000,
  });

  const { data: onboardingProgress } = useQuery<any>({
    queryKey: ["/api/onboarding/progress"],
    retry: false,
    staleTime: 30_000,
  });

  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const { data: sub, isLoading: subLoading } = useQuery<any>({
    queryKey: ["/api/billing/subscription-details"],
    staleTime: 30_000,
  });

  const { data: pricing } = useQuery<any>({
    queryKey: ["/api/billing/platform-pricing"],
    staleTime: 60_000,
  });

  const { data: teamMembers = [], isLoading: teamLoading, refetch: refetchTeam } = useQuery<any[]>({
    queryKey: ["/api/team/members"],
    retry: false,
    staleTime: 30_000,
  });

  const languageMutation = useMutation({
    mutationFn: async (language: "en" | "es") => {
      const res = await apiRequest("PATCH", "/api/account/language", { language });
      return res.json();
    },
    onSuccess: (updated: any) => {
      const lang = updated?.language === "es" ? "es" : "en";
      i18n.changeLanguage(lang);
      setStoredLanguage(lang);
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: t("account:language.updatedTitle"), description: t("account:language.updatedDescription") });
    },
    onError: (err: any) => {
      toast({ title: t("common:error"), description: err.message, variant: "destructive" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/cancel"),
    onSuccess: async () => {
      setCancelDialog(false);
      await apiRequest("POST", "/api/auth/logout");
      window.location.href = "/canceled";
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.failedCancel"), description: err.message, variant: "destructive" });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/reactivate"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/billing/subscription-details"] });
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: t("account:toast.subReactivated"), description: t("account:toast.subReactivatedDesc") });
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.failedReactivate"), description: err.message, variant: "destructive" });
    },
  });

  const redeemMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/billing/redeem-code", { code });
      return res.json();
    },
    onSuccess: (data: any) => {
      setRedeemCode("");
      qc.invalidateQueries({ queryKey: ["/api/billing/subscription-details"] });
      qc.invalidateQueries({ queryKey: ["/api/billing/platform-pricing"] });
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: t("account:toast.codeApplied"), description: data.message });
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.couldNotApply"), description: err.message?.replace(/^\d+:\s*/, ""), variant: "destructive" });
    },
  });

  const portalMutation = useMutation({
    mutationFn: (flow?: string) => apiRequest("POST", "/api/billing/portal", flow ? { flow } : {}),
    onSuccess: (data: any) => {
      window.open(data.url, "_blank");
    },
    onError: () => {
      toast({ title: t("account:toast.couldNotOpenPortal"), variant: "destructive" });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (data: { email: string; role: string; permissions?: typeof invitePermissions }) =>
      apiRequest("POST", "/api/team/invite", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/team/members"] });
      setInviteEmail("");
      setInvitePermissions({ financialAccess: false, lenderAccess: false, inventoryAccess: false, analyticsAccess: false });
      toast({ title: t("account:toast.memberInvited"), description: t("account:toast.memberInvitedDesc") });
      refetchTeam();
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.failedInvite"), description: err.message, variant: "destructive" });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberUserId: string) => apiRequest("DELETE", `/api/team/members/${memberUserId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/team/members"] });
      setRemoveTarget(null);
      toast({ title: t("account:toast.memberRemoved") });
      refetchTeam();
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.failedRemove"), description: err.message, variant: "destructive" });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (memberUserId: string) => apiRequest("POST", "/api/team/invite/resend", { memberUserId }),
    onSuccess: () => {
      toast({ title: t("account:toast.inviteResent"), description: t("account:toast.inviteResentDesc") });
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.failedResend"), description: err.message, variant: "destructive" });
    },
  });

  const permissionsMutation = useMutation({
    mutationFn: ({ memberUserId, permissions }: { memberUserId: string; permissions: Partial<{ financialAccess: boolean; lenderAccess: boolean; inventoryAccess: boolean; analyticsAccess: boolean }> }) =>
      apiRequest("PATCH", `/api/team/members/${memberUserId}/permissions`, permissions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/team/members"] });
      refetchTeam();
    },
    onError: (err: any) => {
      toast({ title: t("account:toast.failedUpdatePerms"), description: err.message, variant: "destructive" });
    },
  });

  const { text: statusText, color: statusColor } = statusLabel(sub?.subscriptionStatus ?? null, t);
  const isCanceling = sub?.subscriptionStatus === "canceling";
  const isActive = sub?.isActive;
  const isOwner = sub?.isOwner;
  const isTeamOwner = !user?.ownerUserId;

  const prog = onboardingProgress?.progress;
  const onboardingCompleted = profile?.onboardingCompleted === true;
  const onboardingStarted = !!prog && prog.currentPhase >= 1;
  const onboardingPhase = Math.min(prog?.currentPhase ?? 0, 8);
  const onboardingPhasePct = Math.min(Math.round((onboardingPhase / 8) * 100), 100);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("account:title")}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t("account:subtitle")}</p>
      </div>

      <Tabs defaultValue="account">
        <TabsList className="mb-4">
          <TabsTrigger value="account" data-testid="tab-account">{t("account:tabAccount")}</TabsTrigger>
          {isTeamOwner && (
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="w-3.5 h-3.5 mr-1.5" />
              {t("account:tabTeam")}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="account" className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                {t("account:accountInfoTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs mb-0.5">{t("account:companyName")}</p>
                  <p className="font-medium">{user?.companyName || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-0.5">{t("account:username")}</p>
                  <p className="font-medium">{user?.username || "—"}</p>
                </div>
                {user?.email && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs mb-0.5">{t("account:email")}</p>
                    <p className="font-medium flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                      {user.email}
                    </p>
                  </div>
                )}
                {user?.accountNumber && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">{t("account:accountNumber")}</p>
                    <p className="font-medium font-mono flex items-center gap-1.5" data-testid="text-account-number">
                      <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                      {user.accountNumber}
                    </p>
                  </div>
                )}
                {user?.createdAt && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">{t("account:memberSince")}</p>
                    <p className="font-medium flex items-center gap-1.5" data-testid="text-member-since">
                      <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                      {new Date(user.createdAt).toLocaleDateString(intlLocale(), { month: "long", year: "numeric" })}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Language Preference */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                {t("account:language.title")}
              </CardTitle>
              <CardDescription>{t("account:language.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Label htmlFor="language-select" className="text-sm text-muted-foreground">
                  {t("common:language")}
                </Label>
                <Select
                  value={user?.language === "es" ? "es" : "en"}
                  onValueChange={(val) => languageMutation.mutate(val as "en" | "es")}
                  disabled={languageMutation.isPending}
                >
                  <SelectTrigger id="language-select" className="w-44" data-testid="select-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en" data-testid="option-language-en">English</SelectItem>
                    <SelectItem value="es" data-testid="option-language-es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Dealership Profile Setup */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-muted-foreground" />
                {t("account:profileSetupTitle")}
              </CardTitle>
              <CardDescription>
                {onboardingCompleted
                  ? t("account:profileSetupCompleteDesc")
                  : onboardingStarted
                  ? t("account:profileSetupProgressDesc")
                  : t("account:profileSetupStartDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(profile || onboardingStarted) ? (
                <>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {onboardingCompleted ? t("account:onboardingComplete") : t("account:phaseOf", { phase: Math.min(onboardingPhase, 8) })}
                      </span>
                      <span className="font-medium" data-testid="text-onboarding-phase">{onboardingPhasePct}%</span>
                    </div>
                    <Progress value={onboardingPhasePct} className="h-2" data-testid="progress-onboarding" />
                  </div>
                  {profile && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {profile?.dealershipName && (
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">{t("account:dealershipName")}</p>
                          <p className="font-medium" data-testid="text-dealership-name">{profile.dealershipName}</p>
                        </div>
                      )}
                      {profile?.inventoryType && (
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">{t("account:inventoryType")}</p>
                          <p className="font-medium capitalize" data-testid="text-inventory-type">{profile.inventoryType.replace("_", " & ")}</p>
                        </div>
                      )}
                      {profile?.address && (
                        <div className="col-span-2">
                          <p className="text-muted-foreground text-xs mb-0.5">{t("account:address")}</p>
                          <p className="font-medium" data-testid="text-dealership-address">{profile.address}</p>
                        </div>
                      )}
                      {profile?.avgSalesPerMonth && (
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">{t("account:monthlySalesTarget")}</p>
                          <p className="font-medium" data-testid="text-avg-sales">{t("account:unitsPerMo", { count: profile.avgSalesPerMonth })}</p>
                        </div>
                      )}
                      {profile?.totalHeadcount && (
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">{t("account:totalHeadcount")}</p>
                          <p className="font-medium" data-testid="text-headcount">{t("account:people", { count: profile.totalHeadcount })}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    variant={onboardingCompleted ? "outline" : "default"}
                    size="sm"
                    onClick={() => setLocation(onboardingCompleted ? "/onboarding?mode=review" : "/onboarding")}
                    data-testid="button-continue-onboarding"
                    className="gap-1.5"
                  >
                    {onboardingCompleted ? t("account:reviewUpdateProfile") : t("account:continueSetup")}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setLocation("/onboarding")}
                  data-testid="button-start-onboarding"
                  className="gap-1.5"
                >
                  {t("account:startProfileSetup")}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                {t("account:needHelp")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                {t("account:contactDesc")}
                {user?.accountNumber && (
                  <> {t("account:contactAccountNumBefore")} <span className="font-mono font-medium">{user.accountNumber}</span> {t("account:contactAccountNumAfter")}</>
                )}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setContactOpen(true)}
                data-testid="button-contact-support"
              >
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                {t("account:contactSupport")}
              </Button>
            </CardContent>
          </Card>


          {/* Subscription */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                {t("account:subscription")}
              </CardTitle>
              <CardDescription>
                {t("account:subDescBrand")} · {pricing ? t("account:subDescPerMonth", { price: pricing.basePrice }) : t("account:subDescLoading")}
                {pricing && pricing.seatCount > 1 && ` ${t("account:subDescSeatAddon", { count: pricing.seatCount - 1, price: pricing.seatPrice })}`}
                {pricing?.hasOverride && <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">({pricing.overrideLabel || t("account:subDescCustomRate")})</span>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {subLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" /> {t("account:loadingSubDetails")}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor}`}>
                      {sub?.subscriptionStatus === "active" && <CheckCircle2 className="w-3.5 h-3.5" />}
                      {sub?.subscriptionStatus === "trialing" && <Calendar className="w-3.5 h-3.5" />}
                      {sub?.subscriptionStatus === "canceling" && <AlertTriangle className="w-3.5 h-3.5" />}
                      {(sub?.subscriptionStatus === "canceled" || sub?.subscriptionStatus === "past_due") && <XCircle className="w-3.5 h-3.5" />}
                      {statusText}
                    </span>
                    {pricing && (
                      <span className="text-xs text-muted-foreground" data-testid="text-seat-count">
                        {t("account:seatCount", { count: pricing.seatCount })}
                      </span>
                    )}
                  </div>

                  {!isOwner && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {sub?.currentPeriodStart && (
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">{t("account:billingStarted")}</p>
                          <p className="font-medium">{fmt(sub.currentPeriodStart)}</p>
                        </div>
                      )}
                      {sub?.currentPeriodEnd && (
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">
                            {isCanceling ? t("account:accessEnds") : t("account:nextBillingDate")}
                          </p>
                          <p className="font-medium">{fmt(sub.currentPeriodEnd)}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {isCanceling && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-800 dark:text-yellow-300">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{t("account:cancelingBanner")}</span>
                    </div>
                  )}

                  {sub?.subscriptionStatus === "trialing" && sub?.trialEnd && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300" data-testid="banner-trial-info">
                      <Calendar className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{t("account:trialBannerPrefix")} <span className="font-medium">{fmt(sub.trialEnd)}</span>{t("account:trialBannerSuffix")}</span>
                    </div>
                  )}

                  {!isOwner && user?.stripeSubscriptionId && (sub?.subscriptionStatus === "active" || sub?.subscriptionStatus === "trialing") && (
                    <div className="pt-1 border-t">
                      <p className="text-xs text-muted-foreground mt-2 mb-1.5">{t("account:discountPrompt")}</p>
                      <div className="flex gap-2">
                        <Input
                          value={redeemCode}
                          onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                          placeholder={t("account:enterCode")}
                          className="h-8 text-sm font-mono uppercase"
                          data-testid="input-redeem-code"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!redeemCode.trim() || redeemMutation.isPending}
                          onClick={() => redeemMutation.mutate(redeemCode.trim())}
                          data-testid="button-redeem-code"
                        >
                          {redeemMutation.isPending ? t("account:applying") : t("account:apply")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!isOwner && (
                    <div className="flex flex-col gap-2 pt-1">
                      {user?.stripeCustomerId && (
                        <Button
                          size="sm"
                          onClick={() => portalMutation.mutate(undefined)}
                          disabled={portalMutation.isPending}
                          data-testid="button-manage-subscription"
                        >
                          {portalMutation.isPending ? t("account:opening") : t("account:manageSubscription")}
                        </Button>
                      )}
                      {user?.stripeCustomerId && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => portalMutation.mutate("payment_method_update")}
                          disabled={portalMutation.isPending}
                          data-testid="button-change-payment-method"
                        >
                          {t("account:changePaymentMethod")}
                        </Button>
                      )}
                      {isCanceling && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reactivateMutation.mutate()}
                          disabled={reactivateMutation.isPending}
                          data-testid="button-reactivate-subscription"
                        >
                          {reactivateMutation.isPending ? t("account:reactivating") : t("account:reactivateSubscription")}
                        </Button>
                      )}
                      {isActive && !isCanceling && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCancelDialog(true)}
                          data-testid="button-cancel-subscription"
                          className="text-destructive border-destructive/30 hover:bg-destructive/5"
                        >
                          {t("account:cancelSubscription")}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("account:security.title")}</CardTitle>
              <CardDescription>{t("account:security.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-sm">
              <div className="space-y-2">
                <Label htmlFor="current-password">{t("account:security.currentPassword")}</Label>
                <Input id="current-password" type="password" autoComplete="current-password" value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)} data-testid="input-current-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t("account:security.newPassword")}</Label>
                <Input id="new-password" type="password" autoComplete="new-password" value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)} data-testid="input-new-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t("account:security.confirmPassword")}</Label>
                <Input id="confirm-password" type="password" autoComplete="new-password" value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)} data-testid="input-confirm-password" />
              </div>
              <Button
                size="sm"
                disabled={changePasswordMutation.isPending || !pwCurrent || pwNew.length < 8 || pwNew !== pwConfirm}
                onClick={() => changePasswordMutation.mutate()}
                data-testid="button-change-password"
              >
                {changePasswordMutation.isPending ? t("account:security.changing") : t("account:security.changeButton")}
              </Button>
              {pwNew.length > 0 && pwNew.length < 8 && (
                <p className="text-xs text-muted-foreground">{t("account:security.minLength")}</p>
              )}
              {pwConfirm.length > 0 && pwNew !== pwConfirm && (
                <p className="text-xs text-destructive">{t("account:security.mismatch")}</p>
              )}
            </CardContent>
          </Card>

          {user?.role === "subscriber" && !user?.ownerUserId && (
            <DeanTrainingConsentCard />
          )}
        </TabsContent>

        {isTeamOwner && (
          <TabsContent value="team" className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-muted-foreground" />
                  {t("account:team.inviteTeamMember")}
                </CardTitle>
                <CardDescription>
                  {t("account:team.inviteDesc", { price: pricing?.seatPrice ?? "49" })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 sm:col-span-1 space-y-1">
                    <Label className="text-xs">{t("account:team.emailAddress")}</Label>
                    <Input
                      type="email"
                      placeholder={t("account:team.emailPlaceholder")}
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      data-testid="input-invite-email"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t("account:team.accessLevel")}</Label>
                    <Select value={inviteRole} onValueChange={(v: "viewer" | "editor") => setInviteRole(v)}>
                      <SelectTrigger data-testid="select-invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">{t("account:team.viewOnly")}</SelectItem>
                        <SelectItem value="editor">{t("account:team.fullAccess")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("account:team.initialPermissions")}</p>
                  <p className="text-xs text-muted-foreground">{t("account:team.permissionsHint")}</p>
                  {[
                    { key: "financialAccess" as const, label: t("account:perm.financial"), desc: t("account:perm.financialDesc"), icon: FileText },
                    { key: "lenderAccess" as const, label: t("account:perm.lender"), desc: t("account:perm.lenderDesc"), icon: Building2 },
                    { key: "inventoryAccess" as const, label: t("account:perm.inventory"), desc: t("account:perm.inventoryDesc"), icon: Car },
                    { key: "analyticsAccess" as const, label: t("account:perm.analytics"), desc: t("account:perm.analyticsDesc"), icon: BarChart3 },
                  ].map(({ key, label, desc, icon: Icon }) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium leading-tight">{label}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight truncate">{desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={invitePermissions[key]}
                        onCheckedChange={(v) => setInvitePermissions(p => ({ ...p, [key]: v }))}
                        data-testid={`switch-invite-${key}`}
                      />
                    </div>
                  ))}
                </div>

                <Button
                  size="sm"
                  onClick={() => inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole, permissions: invitePermissions })}
                  disabled={inviteMutation.isPending || !inviteEmail.trim()}
                  data-testid="button-invite-member"
                >
                  {inviteMutation.isPending ? t("account:team.inviting") : t("account:team.inviteMember")}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  {t("account:team.teamMembers")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {teamLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="w-4 h-4 animate-spin" /> {t("account:team.loading")}
                  </div>
                ) : teamMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("account:team.noTeamMembers")}</p>
                ) : (
                  <div className="space-y-2">
                    {teamMembers.map((m: any) => {
                      const isExpanded = expandedMember === m.id;
                      return (
                        <div key={m.id} className="rounded-lg border overflow-hidden" data-testid={`row-member-${m.id}`}>
                          <div className="flex items-start justify-between p-3 gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">{m.username || m.invitedEmail}</p>
                              <p className="text-xs text-muted-foreground">{m.email || m.invitedEmail} · {m.role === "editor" ? t("account:team.fullAccess") : t("account:team.viewOnly")} · {m.status === "active" ? t("account:team.active") : t("account:team.pendingInvite")}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {!m.inviteAcceptedAt && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-muted-foreground hover:text-foreground h-8 px-2 gap-1 text-xs"
                                  onClick={() => resendInviteMutation.mutate(m.memberUserId)}
                                  disabled={resendInviteMutation.isPending}
                                  title={t("account:team.resendTitle")}
                                  data-testid={`button-resend-invite-${m.id}`}
                                >
                                  <Send className="w-3 h-3" />
                                  {t("account:team.resend")}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-muted-foreground hover:text-foreground h-8 px-2 gap-1 text-xs"
                                onClick={() => setExpandedMember(isExpanded ? null : m.id)}
                                data-testid={`button-permissions-${m.id}`}
                              >
                                {t("account:team.permissions")} {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                                onClick={() => setRemoveTarget(m.memberUserId)}
                                data-testid={`button-remove-member-${m.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t px-3 py-3 bg-muted/20 space-y-2">
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold mb-2">{t("account:team.featureAccess")}</p>
                              {[
                                { key: "financialAccess" as const, label: t("account:perm.financial"), desc: t("account:perm.financialDesc"), icon: FileText },
                                { key: "lenderAccess" as const, label: t("account:perm.lender"), desc: t("account:perm.lenderDescShort"), icon: Building2 },
                                { key: "inventoryAccess" as const, label: t("account:perm.inventory"), desc: t("account:perm.inventoryDesc"), icon: Car },
                                { key: "analyticsAccess" as const, label: t("account:perm.analytics"), desc: t("account:perm.analyticsDesc"), icon: BarChart3 },
                              ].map(({ key, label, desc, icon: Icon }) => (
                                <div key={key} className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-xs font-medium leading-tight">{label}</p>
                                      <p className="text-[10px] text-muted-foreground leading-tight">{desc}</p>
                                    </div>
                                  </div>
                                  <Switch
                                    checked={!!m[key]}
                                    onCheckedChange={(checked) => permissionsMutation.mutate({ memberUserId: m.memberUserId, permissions: { [key]: checked } })}
                                    disabled={permissionsMutation.isPending}
                                    data-testid={`switch-${key}-${m.id}`}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

      </Tabs>

      {/* Remove Member Dialog */}
      <Dialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("account:removeMemberTitle")}</DialogTitle>
            <DialogDescription>
              {t("account:removeMemberDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} data-testid="button-cancel-remove-member">{t("account:cancel")}</Button>
            <Button
              variant="destructive"
              onClick={() => removeTarget && removeMemberMutation.mutate(removeTarget)}
              disabled={removeMemberMutation.isPending}
              data-testid="button-confirm-remove-member"
            >
              {removeMemberMutation.isPending ? t("account:removing") : t("account:removeMember")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("account:cancelSubTitle")}</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">{t("account:cancelSubDesc1Bold")}</strong> {t("account:cancelSubDesc1Rest")}
                </p>
                <p>{t("account:cancelSubDesc2")}</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)} data-testid="button-keep-subscription">
              {t("account:keepSubscription")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              data-testid="button-confirm-cancel"
            >
              {cancelMutation.isPending ? t("account:canceling") : t("account:confirmCancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContactUsModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        userName={user?.companyName || user?.username || ""}
        userEmail={user?.email || ""}
      />
    </div>
  );
}
